import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmail from 'validator/es/lib/isEmail';
import ReviewUser from 'components/ReviewUser/ReviewUser';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { approveEmployeeRequest, Employee } from 'store/Employee';
import { IStore } from 'store/types';
import { EmployeeReviewFieldKey, IEmployee, IEmployeeReviewField } from 'types';
import { formatDateForUI } from 'utils/date';
import { formatMobile } from 'utils/strings';

interface IReviewUserScreenProps {
  employee?: IEmployee;
  onClose?: () => void;
  onDeny?: () => void;
  onApprove?: () => void;
  visible: boolean;
}

const ReviewUserScreen = ({ employee, onClose, onDeny, onApprove, visible }: IReviewUserScreenProps) => {
  const dispatch = useDispatch();
  const isReviewEmployeeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_REQUEST));

  const reviewEmployeeStatusFailures = employee?.approvalFailures;
  const firstNameFailure = (reviewEmployeeStatusFailures || []).find((failure) => failure.field === 'first_name');
  const lastNameFailure = (reviewEmployeeStatusFailures || []).find((failure) => failure.field === 'last_name');
  const identityFailure = (reviewEmployeeStatusFailures || []).find((failure) => failure.field === 'invite_recipient');

  const formatIdentity = (identity: string): string => {
    if (isEmail(identity)) return identity;
    return formatMobile(identity) || '';
  };

  const reviewEmployeeInviteRecipient = employee?.inviteRecipient || '';
  const reviewEmployeeFirstName = employee?.firstName || '';
  const reviewEmployeeLastName = employee?.lastName || '';
  const matchedIdentityVal = formatIdentity(reviewEmployeeInviteRecipient);

  const reviewObject: Record<EmployeeReviewFieldKey, IEmployeeReviewField> = {
    [EmployeeReviewFieldKey.FIRST_NAME]: firstNameFailure
      ? {
          employee: firstNameFailure.employeeValue as string,
          user: firstNameFailure.userValue as string,
        }
      : {
          employee: reviewEmployeeFirstName,
          user: reviewEmployeeFirstName,
        },
    [EmployeeReviewFieldKey.LAST_NAME]: lastNameFailure
      ? {
          employee: lastNameFailure.employeeValue as string,
          user: lastNameFailure.userValue as string,
        }
      : {
          employee: reviewEmployeeLastName,
          user: reviewEmployeeLastName,
        },
    [EmployeeReviewFieldKey.IDENTITY_RECIPIENT]: identityFailure
      ? {
          employee: formatIdentity(identityFailure.employeeValue || ''),
          user: formatIdentity(identityFailure.userValue || ''),
        }
      : {
          employee: matchedIdentityVal,
          user: matchedIdentityVal,
        },
    [EmployeeReviewFieldKey.DATE_OF_BIRTH]: {
      employee: '',
      user: employee?.user?.dateOfBirth ? formatDateForUI(employee.user.dateOfBirth) : '',
    },
  };

  const onApproveSuccess = () => {
    onApprove?.();
    onClose?.();
  };

  return (
    <ReviewUser
      avatarUrl={employee?.user?.avatarUrl}
      reviewObject={reviewObject}
      isActive={visible}
      isLoading={isReviewEmployeeLoading}
      onClose={() => onClose?.()}
      onIgnore={() => onDeny?.()}
      onApprove={() => {
        if (employee?.id) {
          dispatch(approveEmployeeRequest(employee.id, onApproveSuccess));
        }
      }}
      userName={employee?.name || ''}
    />
  );
};

export default ReviewUserScreen;
